/**
 * React hooks for sales tracking operations.
 * @module hooks/use-sales
 *
 * @description
 * Provides hooks for:
 * - Fetching and updating demo sales data
 * - Managing notes, follow-ups, and calls
 * - Sales dashboard statistics
 * - Pipeline stage management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import {
  fetchDemoWithRelations,
  fetchDemosWithFilters,
  updateDemoSales,
  updateDemoFields,
  updateDemoPipelineStage,
  fetchDemoNotes,
  createNote,
  updateNote,
  toggleNotePin,
  deleteNote,
  fetchDemoFollowUps,
  fetchAllPendingFollowUps,
  fetchOverdueFollowUps,
  createFollowUp,
  updateFollowUp,
  completeFollowUp,
  deleteFollowUp,
  fetchDemoCalls,
  fetchSalesDashboardStats,
  deleteDemo,
} from '@/lib/api';
import type {
  DemoWithRelations,
  Demo,
  DemoNote,
  DemoFollowUp,
  DemoCall,
  DemoFilters,
  DemoSort,
  CreateNoteRequest,
  UpdateNoteRequest,
  CreateFollowUpRequest,
  UpdateFollowUpRequest,
  CompleteFollowUpRequest,
  UpdateDemoSalesRequest,
  UpdateDemoFieldsRequest,
  PipelineStage,
  SalesDashboardStats,
  LostReason,
  DemoConversionRequest,
  DemoConversionResponse,
} from '@/types';
import { onboardingKeys } from './use-onboarding';

// ============ Query Keys ============

export const salesKeys = {
  all: ['sales'] as const,
  demos: () => [...salesKeys.all, 'demos'] as const,
  demo: (id: string) => [...salesKeys.demos(), id] as const,
  demoWithRelations: (id: string) => [...salesKeys.demo(id), 'relations'] as const,
  notes: (demoId: string) => [...salesKeys.demo(demoId), 'notes'] as const,
  followUps: (demoId: string) => [...salesKeys.demo(demoId), 'follow-ups'] as const,
  calls: (demoId: string) => [...salesKeys.demo(demoId), 'calls'] as const,
  allFollowUps: () => [...salesKeys.all, 'all-follow-ups'] as const,
  overdueFollowUps: () => [...salesKeys.all, 'overdue-follow-ups'] as const,
  stats: () => [...salesKeys.all, 'stats'] as const,
};

// ============ Demo Hooks ============

/**
 * Fetches a demo with all related data (notes, follow-ups, calls, activities).
 */
export function useDemoWithRelations(demoId: string) {
  return useQuery<DemoWithRelations | null>({
    queryKey: salesKeys.demoWithRelations(demoId),
    queryFn: () => fetchDemoWithRelations(demoId),
    enabled: !!demoId,
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Fetches demos with filters and sorting.
 */
export function useFilteredDemos(
  filters: DemoFilters = {},
  sort: DemoSort = { field: 'last_activity_at', direction: 'desc' },
  limit = 50
) {
  return useQuery<Demo[]>({
    queryKey: [...salesKeys.demos(), filters, sort, limit],
    queryFn: () => fetchDemosWithFilters(filters, sort, limit),
    staleTime: 30 * 1000,
  });
}

/**
 * Updates demo sales tracking fields.
 */
export function useUpdateDemoSales() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ demoId, updates }: { demoId: string; updates: UpdateDemoSalesRequest }) =>
      updateDemoSales(demoId, updates),
    onSuccess: (data, { demoId }) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: salesKeys.demo(demoId) });
      queryClient.invalidateQueries({ queryKey: salesKeys.demos() });
      queryClient.invalidateQueries({ queryKey: salesKeys.stats() });
      toast.success('Lead updated');
    },
    onError: (error) => {
      toast.error('Failed to update lead', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });
}

/**
 * Updates demo business/content fields (services, prompt, website data, etc.).
 */
export function useUpdateDemoFields() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ demoId, updates }: { demoId: string; updates: UpdateDemoFieldsRequest }) =>
      updateDemoFields(demoId, updates),
    onSuccess: (data, { demoId }) => {
      // Cancel any in-flight queries that might overwrite our optimistic update
      queryClient.cancelQueries({ queryKey: salesKeys.demo(demoId) });
      // Immediately update the cache with returned data so view mode shows new values
      queryClient.setQueryData(
        salesKeys.demoWithRelations(demoId),
        (old: DemoWithRelations | null | undefined) => old ? { ...old, ...data } : old,
      );
      // Also invalidate to ensure full refetch with relations
      queryClient.invalidateQueries({ queryKey: salesKeys.demo(demoId) });
      queryClient.invalidateQueries({ queryKey: salesKeys.demos() });
      toast.success('Demo updated');
    },
    onError: (error) => {
      toast.error('Failed to update demo', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });
}

/**
 * Updates just the pipeline stage of a demo.
 */
export function useUpdatePipelineStage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ demoId, stage }: { demoId: string; stage: PipelineStage }) =>
      updateDemoPipelineStage(demoId, stage),
    onMutate: async ({ demoId, stage }) => {
      // Cancel outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: salesKeys.demos() });

      // Snapshot previous value for rollback
      const previousDemos = queryClient.getQueryData(salesKeys.demos());

      // Optimistically update the demo's pipeline_stage in cache
      queryClient.setQueriesData<Demo[]>(
        { queryKey: salesKeys.demos() },
        (old) => old?.map((d) => d.id === demoId ? { ...d, pipeline_stage: stage, last_activity_at: new Date().toISOString() } : d)
      );

      return { previousDemos };
    },
    onSuccess: (data, { demoId, stage }) => {
      queryClient.invalidateQueries({ queryKey: salesKeys.demo(demoId) });
      queryClient.invalidateQueries({ queryKey: salesKeys.demos() });
      queryClient.invalidateQueries({ queryKey: salesKeys.stats() });
      toast.success(`Stage updated to ${formatStage(stage)}`);
    },
    onError: (error, _variables, context) => {
      // Rollback to previous state on error
      if (context?.previousDemos) {
        queryClient.setQueriesData({ queryKey: salesKeys.demos() }, context.previousDemos);
      }
      toast.error('Failed to update stage', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });
}

/**
 * Deletes a demo from the database.
 */
export function useDeleteDemo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (demoId: string) => deleteDemo(demoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: salesKeys.demos() });
      queryClient.invalidateQueries({ queryKey: salesKeys.stats() });
      toast.success('Lead deleted');
    },
    onError: (error) => {
      toast.error('Failed to delete lead', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });
}

// ============ Notes Hooks ============

/**
 * Fetches all notes for a demo.
 */
export function useDemoNotes(demoId: string) {
  return useQuery<DemoNote[]>({
    queryKey: salesKeys.notes(demoId),
    queryFn: () => fetchDemoNotes(demoId),
    enabled: !!demoId,
    staleTime: 30 * 1000,
  });
}

/**
 * Creates a new note.
 */
export function useCreateNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateNoteRequest) => createNote(request),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: salesKeys.notes(data.demo_id) });
      queryClient.invalidateQueries({ queryKey: salesKeys.demoWithRelations(data.demo_id) });
      toast.success('Note added');
    },
    onError: (error) => {
      toast.error('Failed to add note', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });
}

/**
 * Updates an existing note.
 */
export function useUpdateNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ noteId, demoId, updates }: { noteId: string; demoId: string; updates: UpdateNoteRequest }) =>
      updateNote(noteId, updates),
    onSuccess: (data, { demoId }) => {
      queryClient.invalidateQueries({ queryKey: salesKeys.notes(demoId) });
      queryClient.invalidateQueries({ queryKey: salesKeys.demoWithRelations(demoId) });
      toast.success('Note updated');
    },
    onError: (error) => {
      toast.error('Failed to update note', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });
}

/**
 * Toggles the pinned status of a note.
 */
export function useToggleNotePin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ noteId, demoId, isPinned }: { noteId: string; demoId: string; isPinned: boolean }) =>
      toggleNotePin(noteId, isPinned),
    onSuccess: (data, { demoId, isPinned }) => {
      queryClient.invalidateQueries({ queryKey: salesKeys.notes(demoId) });
      queryClient.invalidateQueries({ queryKey: salesKeys.demoWithRelations(demoId) });
      toast.success(isPinned ? 'Note pinned' : 'Note unpinned');
    },
    onError: (error) => {
      toast.error('Failed to update note', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });
}

/**
 * Deletes a note.
 */
export function useDeleteNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ noteId, demoId }: { noteId: string; demoId: string }) => deleteNote(noteId),
    onSuccess: (_, { demoId }) => {
      queryClient.invalidateQueries({ queryKey: salesKeys.notes(demoId) });
      queryClient.invalidateQueries({ queryKey: salesKeys.demoWithRelations(demoId) });
      toast.success('Note deleted');
    },
    onError: (error) => {
      toast.error('Failed to delete note', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });
}

// ============ Follow-up Hooks ============

/**
 * Fetches all follow-ups for a demo.
 */
export function useDemoFollowUps(demoId: string) {
  return useQuery<DemoFollowUp[]>({
    queryKey: salesKeys.followUps(demoId),
    queryFn: () => fetchDemoFollowUps(demoId),
    enabled: !!demoId,
    staleTime: 30 * 1000,
  });
}

/**
 * Fetches all pending follow-ups across all demos.
 */
export function useAllPendingFollowUps() {
  return useQuery({
    queryKey: salesKeys.allFollowUps(),
    queryFn: fetchAllPendingFollowUps,
    staleTime: 60 * 1000, // 1 minute
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });
}

/**
 * Fetches overdue follow-ups.
 */
export function useOverdueFollowUps() {
  return useQuery({
    queryKey: salesKeys.overdueFollowUps(),
    queryFn: fetchOverdueFollowUps,
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });
}

/**
 * Creates a new follow-up.
 */
export function useCreateFollowUp() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateFollowUpRequest) => createFollowUp(request),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: salesKeys.followUps(data.demo_id) });
      queryClient.invalidateQueries({ queryKey: salesKeys.demoWithRelations(data.demo_id) });
      queryClient.invalidateQueries({ queryKey: salesKeys.allFollowUps() });
      queryClient.invalidateQueries({ queryKey: salesKeys.stats() });
      toast.success('Follow-up scheduled');
    },
    onError: (error) => {
      toast.error('Failed to create follow-up', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });
}

/**
 * Updates an existing follow-up.
 */
export function useUpdateFollowUp() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ followUpId, demoId, updates }: { followUpId: string; demoId: string; updates: UpdateFollowUpRequest }) =>
      updateFollowUp(followUpId, updates),
    onSuccess: (data, { demoId }) => {
      queryClient.invalidateQueries({ queryKey: salesKeys.followUps(demoId) });
      queryClient.invalidateQueries({ queryKey: salesKeys.demoWithRelations(demoId) });
      queryClient.invalidateQueries({ queryKey: salesKeys.allFollowUps() });
      toast.success('Follow-up updated');
    },
    onError: (error) => {
      toast.error('Failed to update follow-up', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });
}

/**
 * Marks a follow-up as completed.
 */
export function useCompleteFollowUp() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ followUpId, demoId, request }: { followUpId: string; demoId: string; request?: CompleteFollowUpRequest }) =>
      completeFollowUp(followUpId, request),
    onSuccess: (data, { demoId }) => {
      queryClient.invalidateQueries({ queryKey: salesKeys.followUps(demoId) });
      queryClient.invalidateQueries({ queryKey: salesKeys.demoWithRelations(demoId) });
      queryClient.invalidateQueries({ queryKey: salesKeys.allFollowUps() });
      queryClient.invalidateQueries({ queryKey: salesKeys.overdueFollowUps() });
      queryClient.invalidateQueries({ queryKey: salesKeys.stats() });
      toast.success('Follow-up completed');
    },
    onError: (error) => {
      toast.error('Failed to complete follow-up', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });
}

/**
 * Deletes a follow-up.
 */
export function useDeleteFollowUp() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ followUpId, demoId }: { followUpId: string; demoId: string }) => deleteFollowUp(followUpId),
    onSuccess: (_, { demoId }) => {
      queryClient.invalidateQueries({ queryKey: salesKeys.followUps(demoId) });
      queryClient.invalidateQueries({ queryKey: salesKeys.demoWithRelations(demoId) });
      queryClient.invalidateQueries({ queryKey: salesKeys.allFollowUps() });
      queryClient.invalidateQueries({ queryKey: salesKeys.stats() });
      toast.success('Follow-up deleted');
    },
    onError: (error) => {
      toast.error('Failed to delete follow-up', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });
}

// ============ Call Hooks ============

/**
 * Fetches all calls for a demo.
 */
export function useDemoCalls(demoId: string) {
  return useQuery<DemoCall[]>({
    queryKey: salesKeys.calls(demoId),
    queryFn: () => fetchDemoCalls(demoId),
    enabled: !!demoId,
    staleTime: 30 * 1000,
  });
}

// ============ Stats Hooks ============

/**
 * Fetches sales dashboard statistics.
 */
export function useSalesDashboardStats() {
  return useQuery<SalesDashboardStats>({
    queryKey: salesKeys.stats(),
    queryFn: fetchSalesDashboardStats,
    staleTime: 60 * 1000, // 1 minute
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });
}

// ============ Pipeline Status Actions ============

/**
 * Marks a demo as Won (manual action by sales rep).
 * Does NOT release phone (phone stays for converted tenant).
 */
export function useMarkDemoWon() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ demoId, estimatedValue }: { demoId: string; estimatedValue?: number }) => {
      // Update demo to Won status
      const { error } = await supabase
        .from('demos')
        .update({
          pipeline_stage: 'won',
          estimated_monthly_value: estimatedValue,
          last_activity_at: new Date().toISOString(),
        })
        .eq('id', demoId);

      if (error) throw error;

      // Log activity
      await supabase.from('demo_activity_log').insert({
        demo_id: demoId,
        event_type: 'stage_changed',
        event_data: { to_stage: 'won', trigger: 'manual' },
      });
    },
    onSuccess: (_, { demoId }) => {
      queryClient.invalidateQueries({ queryKey: salesKeys.demo(demoId) });
      queryClient.invalidateQueries({ queryKey: salesKeys.demos() });
      queryClient.invalidateQueries({ queryKey: salesKeys.stats() });
      toast.success('Lead marked as Won!');
    },
    onError: (error) => {
      toast.error('Failed to mark as won', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });
}

/**
 * Marks a demo as Lost with required reason.
 * Releases the phone back to the pool.
 */
export function useMarkDemoLost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      demoId,
      lostReason,
      lostNotes,
      phonePoolId,
    }: {
      demoId: string;
      lostReason: LostReason;
      lostNotes?: string;
      phonePoolId?: string;
    }) => {
      // Update demo to Lost status
      const { error } = await supabase
        .from('demos')
        .update({
          pipeline_stage: 'lost',
          status: 'released',
          lost_reason: lostReason,
          lost_notes: lostNotes,
          released_at: new Date().toISOString(),
          last_activity_at: new Date().toISOString(),
        })
        .eq('id', demoId);

      if (error) throw error;

      // Release phone back to pool if provided
      if (phonePoolId) {
        const cooldownMinutes = 5;
        const cooldownUntil = new Date(Date.now() + cooldownMinutes * 60 * 1000).toISOString();
        await supabase
          .from('demo_phone_pool')
          .update({
            status: 'cooldown',
            current_demo_id: null,
            released_at: new Date().toISOString(),
            cooldown_until: cooldownUntil,
          })
          .eq('id', phonePoolId);
      }

      // Log activity
      await supabase.from('demo_activity_log').insert({
        demo_id: demoId,
        event_type: 'stage_changed',
        event_data: { to_stage: 'lost', lost_reason: lostReason, phone_released: !!phonePoolId },
      });
    },
    onSuccess: (_, { demoId }) => {
      queryClient.invalidateQueries({ queryKey: salesKeys.demo(demoId) });
      queryClient.invalidateQueries({ queryKey: salesKeys.demos() });
      queryClient.invalidateQueries({ queryKey: salesKeys.stats() });
      toast.success('Lead marked as Lost');
    },
    onError: (error) => {
      toast.error('Failed to mark as lost', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });
}

/**
 * Marks a demo for Follow Up (Hybrid approach).
 * Updates BOTH the demo pipeline_stage AND the linked prospect status.
 * Optionally sets a follow-up date.
 */
export function useMarkDemoFollowUp() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ demoId, followUpDate }: { demoId: string; followUpDate?: Date }) => {
      // Update demo pipeline_stage to follow_up with optional follow_up_date
      const { error } = await supabase
        .from('demos')
        .update({
          pipeline_stage: 'follow_up',
          follow_up_date: followUpDate ? followUpDate.toISOString() : null,
          last_activity_at: new Date().toISOString(),
        })
        .eq('id', demoId);

      if (error) {
        throw new Error(error.message || 'Failed to update demo');
      }

      // Find and update linked prospect (don't fail if no prospect found)
      const { data: prospect } = await supabase
        .from('prospects')
        .select('id')
        .eq('demo_id', demoId)
        .maybeSingle();

      if (prospect) {
        const { error: prospectError } = await supabase
          .from('prospects')
          .update({ status: 'follow_up' })
          .eq('id', prospect.id);

        // Log but don't fail if prospect update fails
        if (prospectError) {
          if (import.meta.env.DEV) console.warn('Failed to update prospect status:', prospectError);
        }
      }

      // Log activity (don't fail if this fails)
      const { error: activityError } = await supabase.from('demo_activity_log').insert({
        demo_id: demoId,
        event_type: 'stage_changed',
        event_data: {
          to_stage: 'follow_up',
          follow_up_date: followUpDate?.toISOString(),
          prospect_updated: !!prospect,
        },
      });

      if (activityError) {
        if (import.meta.env.DEV) console.warn('Failed to log activity:', activityError);
      }
    },
    onSuccess: (_, { demoId }) => {
      queryClient.invalidateQueries({ queryKey: salesKeys.demo(demoId) });
      queryClient.invalidateQueries({ queryKey: salesKeys.demos() });
      queryClient.invalidateQueries({ queryKey: salesKeys.stats() });
      queryClient.invalidateQueries({ queryKey: ['prospects'] });
      toast.success('Moved to Follow Up');
    },
    onError: (error) => {
      if (import.meta.env.DEV) console.error('Follow up mutation error:', error);
      toast.error('Failed to move to follow up', {
        description: error instanceof Error ? error.message : String(error),
      });
    },
  });
}

// ============ Helpers ============

/**
 * Formats a pipeline stage for display.
 */
export function formatStage(stage: PipelineStage): string {
  const stageLabels: Record<PipelineStage, string> = {
    new_lead: 'New Lead',
    demo_sent: 'Demo Sent',
    demo_engaged: 'Demo Engaged',
    follow_up: 'Follow-up',
    interested: 'Interested',
    proposal_sent: 'Proposal Sent',
    negotiating: 'Negotiating',
    won: 'Won',
    lost: 'Lost',
    nurture: 'Nurture',
  };
  return stageLabels[stage] || stage;
}

/**
 * Gets the color class for a pipeline stage.
 */
export function getStageColor(stage: PipelineStage): string {
  const stageColors: Record<PipelineStage, string> = {
    new_lead: 'bg-slate-500',
    demo_sent: 'bg-blue-500',
    demo_engaged: 'bg-cyan-500',
    follow_up: 'bg-yellow-500',
    interested: 'bg-orange-500',
    proposal_sent: 'bg-purple-500',
    negotiating: 'bg-pink-500',
    won: 'bg-green-500',
    lost: 'bg-red-500',
    nurture: 'bg-indigo-500',
  };
  return stageColors[stage] || 'bg-gray-500';
}

/**
 * Gets the color class for a lead score.
 */
export function getScoreColor(score: number): string {
  if (score >= 70) return 'text-green-500';
  if (score >= 40) return 'text-yellow-500';
  return 'text-red-500';
}

/**
 * Gets the temperature label for a lead score.
 */
export function getScoreTemperature(score: number): 'hot' | 'warm' | 'cold' {
  if (score >= 70) return 'hot';
  if (score >= 40) return 'warm';
  return 'cold';
}

// ============ Demo → Tenant Conversion Hooks ============

/**
 * Converts a won demo to a production tenant.
 * Uses the `convert_demo_to_tenant` RPC function which:
 * - Creates tenant record with data from demo
 * - Creates onboarding session with pre-populated staged_data
 * - Copies extracted services to business_services table
 * - Marks demo as converted with tenant linkage
 *
 * Supports two paths:
 * - `manual`: Direct conversion via RPC (admin use)
 * - `stripe_checkout`: Generates checkout link, webhook handles conversion
 */
export function useConvertDemoToTenant() {
  const queryClient = useQueryClient();

  return useMutation<DemoConversionResponse, Error, DemoConversionRequest>({
    mutationFn: async (request: DemoConversionRequest) => {
      if (request.conversion_method === 'stripe_checkout') {
        // Stripe path: create checkout session with demo_id in metadata
        const checkoutUrl = import.meta.env.VITE_SUPABASE_URL
          ? `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-checkout-session`
          : null;

        if (!checkoutUrl) {
          throw new Error('Checkout session URL not configured');
        }

        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 30000);
        let response: Response;
        try {
          response = await fetch(checkoutUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            signal: controller.signal,
            body: JSON.stringify({
              demo_id: request.demo_id,
              contact_email: request.contact_email,
              subscription_tier: request.subscription_tier,
            }),
          });
        } catch (err) {
          clearTimeout(timeout);
          if (err instanceof DOMException && err.name === 'AbortError') {
            throw new Error('Checkout session timed out after 30s. Please try again.');
          }
          throw err;
        } finally {
          clearTimeout(timeout);
        }

        if (!response.ok) {
          const err = await response.json().catch(() => ({ error: 'Checkout creation failed' }));
          throw new Error(err.error || 'Failed to create checkout session');
        }

        const result = await response.json();
        return {
          success: true,
          checkout_url: result.url,
        };
      }

      // Manual path: direct RPC conversion (admin/testing use)
      const { data, error } = await supabase.rpc('convert_demo_to_tenant', {
        p_demo_id: request.demo_id,
        p_subscription_tier: request.subscription_tier,
      });

      if (error) throw error;

      const result = data as { success: boolean; tenant_id?: string; error?: string; already_converted?: boolean };

      if (!result.success) {
        throw new Error(result.error || 'Conversion failed');
      }

      return {
        success: true,
        tenant_id: result.tenant_id,
      };
    },
    onSuccess: (result, request) => {
      // Invalidate all related queries
      queryClient.invalidateQueries({ queryKey: salesKeys.demo(request.demo_id) });
      queryClient.invalidateQueries({ queryKey: salesKeys.demos() });
      queryClient.invalidateQueries({ queryKey: salesKeys.stats() });
      queryClient.invalidateQueries({ queryKey: onboardingKeys.queue() });

      if (result.checkout_url) {
        toast.success('Checkout link created — redirecting...');
      } else {
        toast.success('Demo converted to tenant successfully');
      }
    },
    onError: (error) => {
      toast.error('Failed to convert demo', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });
}
